import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { XrSession, Breadcrumb, PerformanceFrame, ExceptionInfo } from '../../../../models/session.model';
import { SessionOverviewPanelComponent } from '../session-overview-panel/session-overview-panel.component';
import { SessionEventsPanelComponent } from '../session-events-panel/session-events-panel.component';
import { SessionTelemetryPanelComponent } from '../session-telemetry-panel/session-telemetry-panel.component';
import { SessionSpatialTargetPanelComponent } from '../session-spatial-target-panel/session-spatial-target-panel.component';
import { SessionNetworkPanelComponent } from '../session-network-panel/session-network-panel.component';
import { SessionSceneHierarchyPanelComponent } from '../session-scene-hierarchy-panel/session-scene-hierarchy-panel.component';

@Component({
  selector: 'app-session-side-panel',
  standalone: true,
  imports: [
    CommonModule,
    SessionOverviewPanelComponent,
    SessionEventsPanelComponent,
    SessionTelemetryPanelComponent,
    SessionSpatialTargetPanelComponent,
    SessionNetworkPanelComponent,
    SessionSceneHierarchyPanelComponent
  ],
  template: `
    <div *ngIf="activePanel"
         class="absolute top-16 right-14 bottom-0 max-w-[calc(100%-80px)] bg-black border border-zaxis-border/95 border-l border-t border-zaxis-border rounded-tl-2xl z-20 flex flex-col shadow-pixel animate-panel-in overflow-hidden transition-all duration-300"
         [ngClass]="sidebarMode === 'full' ? 'w-[380px]' : 'w-[260px]'">

      <!-- Panel Header -->
      <div class="p-4 border-b border-zaxis-border flex-shrink-0">
        <div class="flex items-center justify-between">
          <h3 class="font-bold text-sm flex items-center gap-2" [ngClass]="{'truncate': sidebarMode === 'reduced'}">
            <span *ngIf="activePanel === 'overview'" class="truncate"><i class="ph ph-clipboard-text"></i> Session Overview</span>
            <span *ngIf="activePanel === 'breadcrumbs'" class="truncate"><i class="ph ph-compass"></i> Breadcrumbs</span>
            <span *ngIf="activePanel === 'performance'" class="truncate"><i class="ph ph-lightning"></i> Rendimiento</span>
            <span *ngIf="activePanel === 'spatial'" class="truncate"><i class="ph ph-target"></i> Spatial Target</span>
            <span *ngIf="activePanel === 'network'" class="truncate"><i class="ph ph-globe"></i> API / Network</span>
            <span *ngIf="activePanel === 'objects'" class="truncate"><i class="ph ph-package"></i> Heatmaps por Objeto</span>
          </h3>
          <div class="flex items-center gap-2">
            <button (click)="sidebarModeToggled.emit()" class="text-gray-400 hover:text-white transition-colors"
                    [title]="sidebarMode === 'full' ? 'Reducir panel' : 'Expandir panel'">
              <i class="ph" [ngClass]="sidebarMode === 'full' ? 'ph-arrows-in-line-horizontal' : 'ph-arrows-out-line-horizontal'"></i>
            </button>
            <button (click)="panelClosed.emit()" class="text-gray-400 hover:text-white transition-colors text-lg">&times;</button>
          </div>
        </div>
        <p *ngIf="sidebarMode === 'full'" class="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
          <span *ngIf="activePanel === 'overview'">Resumen general de la sesión: aplicación, dispositivo, versión del SO y estado del tracking XR.</span>
          <span *ngIf="activePanel === 'breadcrumbs'">Registro cronológico de eventos, acciones del usuario e interacciones capturadas durante la sesión.</span>
          <span *ngIf="activePanel === 'performance'">Métricas de rendimiento del dispositivo: cuadros por segundo, tiempos de ciclo de CPU/GPU y frecuencia de refresco del display.</span>
          <span *ngIf="activePanel === 'spatial'">Último objeto 3D con el que el usuario interactuó: nombre, capa, distancia al usuario e interacciones disponibles en el GameObject.</span>
          <span *ngIf="activePanel === 'network'">Consumos de APIs web, código de respuesta HTTP, tiempos de latencia y clasificación de la experiencia del usuario (Apdex).</span>
          <span *ngIf="activePanel === 'objects'">Explora los objetos disponibles en la escena, búscalo por nombre y haz clic para enfocar la cámara y ver su mapa de atención.</span>
        </p>
      </div>

      <!-- Panel Content -->
      <app-session-overview-panel *ngIf="activePanel === 'overview'" [session]="session"></app-session-overview-panel>

      <app-session-events-panel
        *ngIf="activePanel === 'breadcrumbs'"
        [breadcrumbs]="breadcrumbs"
        [currentAbsoluteTime]="currentAbsoluteTime"
        (viewException)="viewException.emit($event)">
      </app-session-events-panel>

      <app-session-telemetry-panel
        *ngIf="activePanel === 'performance'"
        [session]="session"
        [averageFps]="averageFps"
        [currentRefreshRate]="currentRefreshRate"
        [currentCpuLevel]="currentCpuLevel"
        [currentGpuLevel]="currentGpuLevel"
        [currentPerformanceFrame]="currentPerformanceFrame">
      </app-session-telemetry-panel>

      <app-session-spatial-target-panel
        *ngIf="activePanel === 'spatial'"
        [session]="session">
      </app-session-spatial-target-panel>

      <app-session-network-panel
        *ngIf="activePanel === 'network'"
        [session]="session"
        [isLoadingNetworkRequests]="isLoadingNetworkRequests">
      </app-session-network-panel>

      <app-session-scene-hierarchy-panel
        *ngIf="activePanel === 'objects'"
        [objects]="objects"
        [objectStats]="objectStats"
        [uploadingModel]="uploadingModel"
        (focusObject)="focusObject.emit($event)"
        (linkModel)="linkModel.emit($event)"
        (uploadModel)="uploadModel.emit($event)">
      </app-session-scene-hierarchy-panel>
    </div>
  `,
  styles: [`
    @keyframes panelIn {
      from { transform: translateX(20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    .animate-panel-in { animation: panelIn 0.2s ease-out; }
  `]
})
export class SessionSidePanelComponent {
  @Input() activePanel: string | null = null;
  @Input() sidebarMode: 'full' | 'reduced' = 'full';
  @Input() session: XrSession | null = null;
  @Input() breadcrumbs: Breadcrumb[] = [];
  @Input() currentTime = 0;
  @Input() currentAbsoluteTime = 0;
  @Input() averageFps = 0;
  @Input() currentRefreshRate = 0;
  @Input() currentCpuLevel = 0;
  @Input() currentGpuLevel = 0;
  @Input() currentPerformanceFrame: PerformanceFrame | null = null;
  @Input() isLoadingNetworkRequests = false;
  @Input() objects: any[] = [];
  @Input() objectStats: Record<string, any> = {};
  @Input() uploadingModel: Record<string, boolean> = {};

  @Output() panelClosed = new EventEmitter<void>();
  @Output() sidebarModeToggled = new EventEmitter<void>();
  @Output() viewException = new EventEmitter<Breadcrumb>();
  @Output() focusObject = new EventEmitter<any>();
  @Output() linkModel = new EventEmitter<any>();
  @Output() uploadModel = new EventEmitter<any>();
}
